import { useQuery } from "@tanstack/react-query";
import api from "../api";

// Permission type
export interface Permission {
  permission_id: number;
  permission_name: string;
  permission_sort_order: number;
  permission_status_flg: number;
}

export interface PermissionModule {
  module_id: number;
  module_name: string;
  module_sort_order: number;
  module_status_flg: number;
  permissions: Permission[];
}

interface PermissionsResponse {
  status: number;
  message: string;
  data: PermissionModule[];
}
// API fetcher
// API fetcher
const fetchPermissions = async (): Promise<PermissionModule[]> => {
  try {
    const { data } = await api.get<PermissionsResponse>("/permissions/v1");

    if (Array.isArray(data?.data) && data.data.length > 0) {
      return data.data; // ✅ use API response
    }

    // 🚨 API returned empty → use fallback
    return hardcodedPermissions;
  } catch (error) {
    console.error("Failed to fetch permissions, using fallback:", error);
    return hardcodedPermissions; // 🚨 API error → use fallback
  }
};

// Hardcoded fallback
const hardcodedPermissions: PermissionModule[] = [
  {
    module_id: 10,
    module_name: "Tickets",
    module_sort_order: 1,
    module_status_flg: 1,
    permissions: [
      {
        permission_id: 1,
        permission_name: "View Tickets",
        permission_sort_order: 1,
        permission_status_flg: 1,
      },
      {
        permission_id: 2,
        permission_name: "Edit Tickets",
        permission_sort_order: 2,
        permission_status_flg: 1,
      },
      {
        permission_id: 3,
        permission_name: "Assign Tickets",
        permission_sort_order: 3,
        permission_status_flg: 1,
      },
      {
        permission_id: 4,
        permission_name: "Merge Tickets",
        permission_sort_order: 4,
        permission_status_flg: 1,
      },
    ],
  },
  {
    module_id: 1,
    module_name: "Dashboard",
    module_sort_order: 1,
    module_status_flg: 1,
    permissions: [
      {
        permission_id: 1,
        permission_name: "View Dashboard",
        permission_sort_order: 1,
        permission_status_flg: 1,
      },
      {
        permission_id: 2,
        permission_name: "Export Widgets",
        permission_sort_order: 2,
        permission_status_flg: 1,
      },
    ],
  },
  {
    module_id: 2,
    module_name: "Tickets",
    module_sort_order: 2,
    module_status_flg: 1,
    permissions: [
      {
        permission_id: 1,
        permission_name: "View Tickets",
        permission_sort_order: 1,
        permission_status_flg: 1,
      },
      {
        permission_id: 2,
        permission_name: "Edit Tickets",
        permission_sort_order: 2,
        permission_status_flg: 1,
      },
      {
        permission_id: 3,
        permission_name: "Assign Tickets",
        permission_sort_order: 3,
        permission_status_flg: 1,
      },
    ],
  },
  {
    module_id: 11,
    module_name: "Users",
    module_sort_order: 2,
    module_status_flg: 1,
    permissions: [
      {
        permission_id: 1,
        permission_name: "View Users",
        permission_sort_order: 1,
        permission_status_flg: 1,
      },
      {
        permission_id: 2,
        permission_name: "Edit Users",
        permission_sort_order: 2,
        permission_status_flg: 1,
      },
      {
        permission_id: 3,
        permission_name: "Assign Roles",
        permission_sort_order: 3,
        permission_status_flg: 1,
      },
      {
        permission_id: 4,
        permission_name: "Delete Users",
        permission_sort_order: 4,
        permission_status_flg: 1,
      },
    ],
  },
  {
    module_id: 12,
    module_name: "Reports",
    module_sort_order: 3,
    module_status_flg: 1,
    permissions: [
      {
        permission_id: 1,
        permission_name: "View Reports",
        permission_sort_order: 1,
        permission_status_flg: 1,
      },
      {
        permission_id: 2,
        permission_name: "Export Reports",
        permission_sort_order: 2,
        permission_status_flg: 1,
      },
      {
        permission_id: 3,
        permission_name: "Schedule Reports",
        permission_sort_order: 3,
        permission_status_flg: 1,
      },
      {
        permission_id: 4,
        permission_name: "Delete Reports",
        permission_sort_order: 4,
        permission_status_flg: 1,
      },
    ],
  },
];

// Hook
export const usePermissions = () =>
  useQuery({
    queryKey: ["permissions"],
    queryFn: fetchPermissions,
    staleTime: 5 * 60 * 1000, // cache 5 mins
  });
